import { useEffect, useMemo } from 'react'

import { useBelowTheFoldData, usePageData } from '../PageDataProvider'
import { MainCategoryCarouselSection } from './MainCategoryCarouselSection'
import { SubcategoryInstructorCarouselSection } from './SubcategoryInstructorCarouselSection/SubcategoryInstructorCarouselSection'
import { WhatYouGet } from '../WhatYouGet'

const INITIAL_VISIBLE_TILES = 3
const MAX_REORDERS = 5 // avoid infinite loop, don't reorder too many times

export const InstructorCarousels = () => {
  const { subcategories } = usePageData()
  const { content } = useBelowTheFoldData()

  type Content = typeof content

  const subcategoryContentData = useMemo(() => {
    const initiallyVisibleSlugs = new Set(
      content.slice(0, INITIAL_VISIBLE_TILES).map((c) => c.slug),
    )

    const subcategoryContent: Record<number, Content> = {}
    subcategories.forEach((subcat) => {
      const subcatContent = content.filter((c) =>
        subcat.courses.includes(c.slug),
      )
      // random with reordering to mitigate repeating tiles
      const reorderedContent = reorder(subcatContent, initiallyVisibleSlugs)
      reorderedContent.forEach((subcatCourse) =>
        initiallyVisibleSlugs.add(subcatCourse.slug),
      )
      subcategoryContent[subcat.id] = reorderedContent
    })
    return subcategoryContent
  }, [content, subcategories])

  useEffect(() => {
    const { hash } = window.location

    if (hash && !!subcategoryContentData) {
      const element = document.querySelector(hash)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [subcategoryContentData])

  return (
    <div className='mc-my-8 container'>
      <MainCategoryCarouselSection />
      <WhatYouGet />
      {subcategories.map((subcategory) => (
        <SubcategoryInstructorCarouselSection
          key={subcategory.id}
          subcatId={subcategory.id}
          subcatSlug={subcategory.slug}
          subcatTitle={subcategory.name}
          content={subcategoryContentData[subcategory.id]}
        />
      ))}
    </div>
  )
}

const reorder = <T extends { slug: string }>(
  arr: Array<T>,
  initiallyVisibleSlugs: Set<string>,
) => {
  const subcatContent = [...arr]
  for (let i = 0; i < Math.min(subcatContent.length, MAX_REORDERS); i++) {
    const firstVisible = subcatContent
      .slice(0, INITIAL_VISIBLE_TILES)
      .findIndex((c) => initiallyVisibleSlugs.has(c.slug))
    if (firstVisible === -1) {
      // nothing initially visible overlaps; continue
      break
    } else {
      const popped = subcatContent.splice(firstVisible, 1)[0]
      subcatContent.push(popped)
    }
  }
  return subcatContent
}
